import { useQuery } from '@tanstack/react-query';
import { useTheBoatDbApi } from '../../providers/useTheBoatDbApi';
import type { Schemas } from '../generated/theboatdb-typed-openapi';
import { isTheBoatDbApiError } from '../theboatdb-api-error';

export type ApiKey = Schemas.ApiKey;

/**
 * What the list endpoint tells a screen, with the transport's envelope removed
 * but nothing else: the keys, and whether this caller may mint another one.
 *
 * `canCreateKeys` is the API's own answer — the same entitlement check `POST`
 * enforces — rather than anything derived here. A screen that guessed at it
 * (from an empty list, or from a subscription read somewhere else) would offer a
 * create button whose only outcome is a 403.
 */
export type ApiKeysResult = {
  keys: ApiKey[];
  canCreateKeys: boolean;
};

/**
 * Defined once, at module scope, so its identity is stable: React Query re-runs
 * `select` whenever the function changes, and an inline arrow would rebuild this
 * object on every render and defeat the memoisation callers depend on.
 */
const selectApiKeys = (response: Schemas.ApiKeyListResponse): ApiKeysResult => ({
  keys: response.data,
  // Tolerated as missing, not typed as optional: the field is part of the
  // contract, but a front-end can deploy ahead of the API it talks to. Falling
  // back to `true` keeps a subscriber's create button working across that
  // window — the create call is still the thing that decides, and it reports a
  // lapsed subscription itself.
  canCreateKeys: response.entitlement?.canCreateKeys ?? true,
});

/**
 * A 30s window is enough to cover a double render without hiding a revoke made
 * in another tab.
 */
const STALE_TIME_MS = 30_000;

const MAX_RETRIES = 2;

/**
 * Whether this failure is worth trying again.
 *
 * A settled answer — a lapsed subscription, a rejected session, a key that is
 * already gone — is not retried: repeating it only delays the screen showing the
 * user what to do about it. `TheBoatDbApiError.isRetryable` is where that list
 * lives, so it is stated once for every hook here rather than per screen.
 */
export function retryTheBoatDbRequest(failureCount: number, error: Error): boolean {
  if (isTheBoatDbApiError(error) && !error.isRetryable) {
    return false;
  }

  return failureCount < MAX_RETRIES;
}

type Options = {
  /**
   * Gate on the caller's auth state. The package cannot read it itself — each app
   * uses a different Clerk SDK — and a query fired while signed out would spend a
   * round trip to be told so.
   */
  enabled?: boolean;
};

/**
 * The signed-in user's API keys, and whether they may create another.
 *
 * Both come from this one request because both are answers about the same
 * moment: entitlement can lapse, and a screen that read it separately would
 * render a list and a create button resolved at two different times.
 */
export const useApiKeysQuery = ({ enabled = true }: Options = {}) => {
  const { tanstackApiClient } = useTheBoatDbApi();

  return useQuery({
    ...tanstackApiClient.get('/v1/keys').queryOptions,
    select: selectApiKeys,
    enabled,
    staleTime: STALE_TIME_MS,
    retry: retryTheBoatDbRequest,
  });
};

/** The key the list query is cached under, for the mutations that invalidate it. */
export const useApiKeysQueryKey = () => {
  const { tanstackApiClient } = useTheBoatDbApi();

  return tanstackApiClient.get('/v1/keys').queryKey;
};
